import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import apiClient, { getErrorMessage } from '../../api/client';
import { useToast } from '../../hooks/useToast';

const TABS = ['Request Logs', 'Sync Logs'];

const IntegrationLogs = () => {
  const { error: showError } = useToast();
  const [tab, setTab] = useState('Request Logs');
  const [requests, setRequests] = useState([]);
  const [syncLogs, setSyncLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      apiClient.get('/integrations/logs/requests'),
      apiClient.get('/integrations/logs/sync'),
    ]).then(([reqRes, syncRes]) => {
      setRequests(reqRes.data.data);
      setSyncLogs(syncRes.data.data);
    }).catch((err) => showError(getErrorMessage(err, 'Could not load integration logs')))
      .finally(() => setLoading(false));
  };

  useEffect(loadAll, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <motion.div className="dashboard-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
      <div className="page-header">
        <h1>Integration Logs</h1>
        <p>Requests and sync activity for connected third-party integrations</p>
      </div>

      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem', color: '#92400e', fontSize: '0.85rem' }}>
        ⚠️ No third-party integrations (accounting software, HMRC MTD, etc.) can be connected in this environment — there's no connection flow built for any yet, so these logs will genuinely stay empty until one exists.
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb' }}>
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '0.75rem 1rem', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: 600, color: tab === t ? '#3b82f6' : '#6b7280',
              borderBottom: tab === t ? '2px solid #3b82f6' : '2px solid transparent',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Request Logs' && (
        <section className="page-section">
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Date</th><th>Direction</th><th>Method</th><th>Endpoint</th><th>Status</th><th>Error</th></tr></thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
                ) : requests.length === 0 ? (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No request logs yet</td></tr>
                ) : requests.map((r) => (
                  <tr key={r.id}>
                    <td>{new Date(r.createdAt).toLocaleString()}</td>
                    <td style={{ textTransform: 'capitalize' }}>{r.direction}</td>
                    <td style={{ fontFamily: 'monospace' }}>{r.httpMethod}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.endpoint}</td>
                    <td><span className="badge" style={{ background: r.isError ? '#ef4444' : '#10b981', color: 'white' }}>{r.responseStatus || '—'}</span></td>
                    <td>{r.isError ? '⚠️' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'Sync Logs' && (
        <section className="page-section">
          <div className="table-container">
            <table className="data-table">
              <thead><tr><th>Started</th><th>Type</th><th>Status</th><th>Records</th><th>Error</th></tr></thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>Loading...</td></tr>
                ) : syncLogs.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>No sync logs yet</td></tr>
                ) : syncLogs.map((s) => (
                  <tr key={s.id}>
                    <td>{new Date(s.startedAt).toLocaleString()}</td>
                    <td style={{ textTransform: 'capitalize' }}>{s.syncType}</td>
                    <td><span className="badge" style={{ background: s.status === 'completed' ? '#10b981' : s.status === 'failed' ? '#ef4444' : '#f59e0b', color: 'white' }}>{s.status}</span></td>
                    <td>{s.recordsProcessed ?? '—'}</td>
                    <td>{s.errorMessage || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </motion.div>
  );
};

export default IntegrationLogs;